// ** React Imports
import { createContext, useMemo, useRef, useState } from 'react'

// ** Config
import { audiencesUrl, audienceImportUrl } from 'src/configs/api'
import { useRouter } from 'next/router'
import * as yup from 'yup'
import PropTypes from 'prop-types'
import Icon from 'src/@core/components/icon'
import { Box } from '@mui/material'
import { useAPI } from 'src/utils/api'
import { fetchAudienceTags } from 'src/utils/audienceTags'
import { useToast } from 'src/@core/components/custom-Toast/customToast'

const MAX_IMPORT_COLUMNS = 25

// Keep in step with identifierDestinations in cxp-be/cdp/src/etl/import/mapping/mapping.go.
const IDENTIFIER_DESTINATIONS = new Set([
  'email_address',
  'phone_number',
  'whatsapp',
  'instagram',
  'facebook',
  'twitter',
  'linkedin',
  'tiktok',
  'external_id'
])

const defaultProvider = {
  selectedAudienceEvents: [],
  setSelectedAudienceEvents: () => null,
  csvData: '',
  setCsvData: () => null,
  csvDataStatus: '',
  setCsvDataStatus: () => null,
  importMethod: '',
  setImportMethod: () => null,
  deleteId: 0,
  setDeleteId: () => null,
  deleteAudienceName: '',
  setDeleteAudienceName: () => null,
  openDelete: false,
  setOpenDelete: () => null,
  selectedColumns: [],
  setSelectedColumns: () => null,
  files: [],
  setFiles: () => null,
  importColumns:[],
  setImportColumns:()=>null,
  mappedColumns: [],
  setMappedColumns: () => null,
  systemColumns: [],
  setSystemColumns: () => null,
  selectedTags: [],
  setSelectedTags: () => null,
  autoFileTags: [],
  setAutoFileTags: () => null,
  tagsData: [],
  setTagsData: () => null,
  rawFileHeadersData: {},
  setRawFileHeadersData: () => null,
  updatedRawFileHeadersData: {},
  setUpdatedRawFileHeadersData: () => null,
  previewData: {},
  setPreviewData: () => null,
  page: 0,
  setPage: () => null,
  rowsPerPage: 25,
  setRowsPerPage: () => null,
  searchTerm: '',
  setSearchTerm: () => null,
  eventSearchTerm: '',
  setEventSearchTerm: () => null,
  rows: [],
  setRows: () => null,
  selectedRows: [],
  setSelectedRows: () => null,
  rowCount: 0,
  setRowCount: () => null,
  eventCursor: null,
  setEventCursor: () => null,
  hasMoreEvents: true,
  setHasMoreEvents: () => null,
  sortOrder: 'desc',
  setSortOrder: () => null,
  sortOrderBy: '',
  setSortOrderBy: () => null,
  audiencesListRow: [],
  setAudiencesListRow: () => null,
  selectedAudience: {},
  setSelectedAudience: () => null,
  selectedAudienceID: '',
  setSelectedAudienceID: () => null,
  systemColumnList: [],
  setSystemColumnList: () => null,
  columnStatusList: [],
  setColumnStatusList: () => null,
  dataStatus: true,
  setDataStatus: () => null,
  maskField: { email: true, phone: true },
  setMaskField: () => null,
  clicked: false,
  setClicked: () => null,
  isUniqueColumnExists: false,
  setIsUniqueColumnExists: () => null,
  piiColumns: [],
  setPiiColumns: () => null,
  openConfirmPii: false,
  setOpenConfirmPii: () => null,
  isPiiChanged: false,
  setIsPiiChanged: () => null,
  listAudience: () => Promise.resolve(),
  audienceFilterList: [],
  setAudienceFilterList: () => null,
  audienceOtherFilterList: [],
  setAudienceOtherFilterList: () => null,
  hasTagFilter: false,
  setHasTagFilter: () => null,
  isNewFileUpdloaded: false,
  setIsNewFileUpdloaded: () => null,
  createSegment: true,
  setCreateSegment: () => null,
  importId:'',
  setImportId:()=> null
}

const AudienceContext = createContext(defaultProvider)

const AudienceProvider = ({ children }) => {
  const [audiencesListRow, setAudiencesListRow] = useState(defaultProvider.audiencesListRow)

  const [selectedAudience, setSelectedAudience] = useState(defaultProvider.selectedAudience)

  const [selectedAudienceID, setSelectedAudienceID] = useState(defaultProvider.selectedAudienceID)

  const [page, setPage] = useState(defaultProvider.page)

  const [rowsPerPage, setRowsPerPage] = useState(defaultProvider.rowsPerPage)

   const [importId,setImportId] = useState(defaultProvider.importId)

  const [searchTerm, setSearchTerm] = useState(defaultProvider.searchTerm)

  const [eventSearchTerm, setEventSearchTerm] = useState(defaultProvider.eventSearchTerm)

  const [rows, setRows] = useState(defaultProvider.rows)

  const [selectedRows, setSelectedRows] = useState(defaultProvider.selectedRows)

  const [sortOrder, setSortOrder] = useState(defaultProvider.sortOrder)

  const [sortOrderBy, setSortOrderBy] = useState(defaultProvider.sortOrderBy)

  const [rowCount, setRowCount] = useState(defaultProvider.rowCount)

  const [nextCursor, setNextCursor] = useState(null)

  // Bumped after deletes/imports so the listing page refetches its current page, filters and page size.
  const [listVersion, setListVersion] = useState(0)
  const refreshAudienceList = () => setListVersion(v => v + 1)
  const listRequestRef = useRef(0)

  const [eventCursor, setEventCursor] = useState(defaultProvider.eventCursor)

  const [hasMoreEvents, setHasMoreEvents] = useState(defaultProvider.hasMoreEvents)

  const [files, setFiles] = useState(defaultProvider.files)

  const [rawFileHeadersData, setRawFileHeadersData] = useState(defaultProvider.rawFileHeadersData)

  const [mappedColumns, setMappedColumns] = useState(defaultProvider.mappedColumns)

  const[importColumns,setImportColumns] = useState(defaultProvider.importColumns)

  const [systemColumns, setSystemColumns] = useState(defaultProvider.systemColumns)

  const [selectedTags, setSelectedTags] = useState(defaultProvider.selectedTags)

  const [autoFileTags, setAutoFileTags] = useState(defaultProvider.autoFileTags)

  const [tagsData, setTagsData] = useState(defaultProvider.tagsData)

  const [systemColumnList, setSystemColumnList] = useState(defaultProvider.systemColumnList)

  const [columnStatusList, setColumnStatusList] = useState(defaultProvider.columnStatusList)

  const [dataStatus, setDataStatus] = useState(defaultProvider.dataStatus)

  const [previewData, setPreviewData] = useState(defaultProvider.previewData)

  const [updatedRawFileHeadersData, setUpdatedRawFileHeadersData] = useState(defaultProvider.updatedRawFileHeadersData)

  const [maskField, setMaskField] = useState(defaultProvider.maskField)

  const [isUniqueColumnExists, setIsUniqueColumnExists] = useState(defaultProvider.isUniqueColumnExists)

  const [selectedColumns, setSelectedColumns] = useState(defaultProvider.selectedColumns)

  const [openDelete, setOpenDelete] = useState(defaultProvider.openDelete)

  const [deleteAudienceName, setDeleteAudienceName] = useState(defaultProvider.deleteAudienceName)

  const [deleteId, setDeleteId] = useState(defaultProvider.deleteId)

  const [piiColumns, setPiiColumns] = useState(defaultProvider.piiColumns)

  const [openConfirmPii, setOpenConfirmPii] = useState(defaultProvider.openConfirmPii)

  const [isPiiChanged, setIsPiiChanged] = useState(defaultProvider.isPiiChanged)

  const [importMethod, setImportMethod] = useState(defaultProvider.importMethod)

  const [csvData, setCsvData] = useState(defaultProvider.csvData)

  const [selectedAudienceEvents, setSelectedAudienceEvents] = useState(defaultProvider.selectedAudienceEvents)
  const [audienceFilterList, setAudienceFilterList] = useState(defaultProvider.audienceFilterList)
  const [audienceOtherFilterList, setAudienceOtherFilterList] = useState(defaultProvider.audienceOtherFilterList)
  const [hasTagFilter, setHasTagFilter] = useState(defaultProvider.hasTagFilter)
  const [isNewFileUploaded, setIsNewFileUploaded] = useState(defaultProvider.isNewFileUploaded)
  const [createSegment, setCreateSegment] = useState(defaultProvider.createSegment)
  const { showSuccess, showError } = useToast()

  // Mirrors cxp-be mapping.isIdentifier: identifierDestinations plus any external_id_<source>.
  // A bare 'external_id' is valid — it is the EXTERNAL_ID_DEFAULT source's key.
  const isIdentifierColumn = destination =>
    IDENTIFIER_DESTINATIONS.has(destination) || /^external_id_.+/.test(destination || '')

  const schema = yup.object().shape({
    email: yup
      .string()
      .test(
        'is-valid-email',
        'Invalid email format',
        value => !value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
      )
      .nullable(),
    firstName: yup
      .string()
      .max(100, 'First Name must be at most 100 characters long')
      .matches(/^[A-Za-z\s]*$/, 'First Name should contain only alphabets and spaces')
      .nullable(),
    lastName: yup
      .string()
      .max(100, 'Last Name must be at most 100 characters long')
      .matches(/^[A-Za-z\s]*$/, 'Last Name should contain only alphabets and spaces')
      .nullable(),
    externalId: yup
      .string()
      .max(100, 'External ID must be at most 100 characters long')
      .matches(/^[A-Za-z0-9._-]*$/, 'External ID should contain only letters, digits, ".", "_" and "-"')
      .nullable(),
    phone: yup
      .string()
      .test(
        'is-valid-phone',
        'Phone number must be 10-15 characters and include only digits and symbols like +, -, (, )',
        value => !value || /^[0-9()+-]{10,15}$/.test(value)
      )
      .nullable(),
    addressLine1: yup.string().max(200, 'Address Line 1 must be at most 200 characters long').nullable(),
    addressLine2: yup.string().max(200, 'Address Line 2 must be at most 200 characters long').nullable(),
    city: yup.string().max(100, 'City must be at most 100 characters long').nullable(),
    zip: yup
      .string()
      .max(25, 'ZIP code must be at most 25 characters long')
      .matches(/^[0-9]*$/, 'ZIP code should contain only numbers')
      .nullable(),
    state: yup.string().max(100, 'State must be at most 100 characters long').nullable(),
    country: yup.string().max(255, 'Country must be at most 255 characters long').nullable(),
    birthday: yup.string().max(255, 'Birthday must be at most 255 characters long').nullable(),
    age: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value
      })
      .positive('Age must be a positive number')
      .integer('Age must be an integer')
      .nullable(),
    tags: yup.array().of(yup.string().max(255, 'Each tag must be at most 255 characters long'))
  })

  const defaultValues = {
    email: '',
    firstName: '',
    lastName: '',
    externalId: '',
    phone: '',
    sex: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    birthday: '',
    age: null
  }

  const router = useRouter()

  const api = useAPI()
  // The CDP list API takes multi-value filters comma-joined: status=active,pending
  const toCsvParam = value => {
    if (Array.isArray(value)) return value.length ? value.join(',') : undefined

    return value || undefined
  }

  // Fallback for responses that only carry the next page in the Link header:
  // Link: </v1/api/cdp/audiences?cursor=...&limit=3>; rel="next", <...>; rel="first"
  const cursorFromLinkHeader = link => {
    const next = (link || '').split(',').find(part => /rel="?\s*next"?/.test(part))
    const match = next?.match(/[?&]cursor=([^&>]+)/)

    return match ? decodeURIComponent(match[1]) : null
  }

  const getAudienceList = async ({
    search,
    status,
    channels,
    tags,
    limit = 25,
    cursor,
    masked,
    sorting,
    page = 1
  } = {}) => {
    const requestId = ++listRequestRef.current
    try {
      const response = await api.get(audiencesUrl, {
        params: {
          search,
          status: toCsvParam(status),
          channels: toCsvParam(channels),
          limit,
          page,
          pageSize: limit,
          cursor,
          masked,
          tags: toCsvParam(tags),
          // Always send an explicit order; without sort_by the API returns no next_cursor.
          sort_by: sorting?.field || 'created_at',
          sort_order: sorting?.field ? sorting?.order : 'desc'
        }
      })
      // A newer request (page click, filter change) was made while this one was in flight.
      if (requestId !== listRequestRef.current) return undefined

      const data = {
        ...response?.data,
        next_cursor: response?.data?.next_cursor || cursorFromLinkHeader(response?.headers?.link)
      }
      setAudiencesListRow((data.audiences ?? []).filter(a => !a?.merged_into))
      // The API only returns total_count on the first page (no cursor); keep the last known total on later pages
      if (data.total_count != null) setRowCount(data.total_count)
      setNextCursor(data.next_cursor ?? null)

      return data
    } catch {}
  }

  const getAudience = async (id, action) => {
    setSelectedAudienceID(id)
    try {
      // The edit form needs the real PII values to prefill and re-save; view/listing stay masked.
      const response = await api.get(`${audiencesUrl}/${id}`, {
        params: { action, ...(action === 'editAudience' ? { masked: false } : {}) }
      })
      const data = response?.data
      if (data?.merged_into) {
        return getAudience(data.merged_into, action)
      }
      setSelectedAudience(data)
    } catch {}
  }

  const saveAudienceData = async audienceData => {
    try {
      const dataPayload ={"attributes":audienceData}
      const response = await api.post(audiencesUrl, dataPayload)
      showSuccess(
        response?.data?.action === 'insert' ? 'Audience added successfully!' : 'Audience updated successfully!'
      )
      setSelectedTags([])
      router.push('/customer-data-platform/all-audience')
      return response?.status
    } catch (error) {
      if (error?.response?.status === 400) {
        showError(error?.response?.data?.detail || 'Could not match this record to an identifier — nothing was saved.')
        return null
      }
      showError(error?.response?.data?.detail || 'Failed to save audience')
      return null
    }
  }

  const getAudienceTags = search => fetchAudienceTags(api, search)

  const getAllTags = async (query = '') => {
    try {
      setTagsData(await getAudienceTags(query))
    } catch {
      // ignore
    }
  }

  const deleteAudience = async id => {
    try {
      const response = await api.delete(`${audiencesUrl}/${id}`)
      if (response?.data?.erased) {
        showSuccess('Audience erased successfully.')
      }
      refreshAudienceList()
    } catch {}
  }


const audienceBulkAction = async (selectedIDList, bulkAction) => {
  try {
    const response = await api.post(`${audiencesUrl}/erase`, { refs: selectedIDList })
    const { erased = [], failed = [], erased_count = 0, failed_count = 0 } = response?.data ?? {}

    if (failed_count > 0) {
      const reasons = failed.map(f => f.reason).join('; ')
      showError(`${failed_count} of ${selectedIDList.length} deletions failed: ${reasons}`)
    }
    if (erased_count > 0) {
      showSuccess(`${erased_count} audience(s) deleted successfully.`)
    }

    refreshAudienceList()
  } catch {}
}
const uploadFilesData = async (formData) => {
 
  try {
    const response = await api.post(
      `${audienceImportUrl}/uploads`,
      formData,
      {
        // Let Axios/browser generate the multipart boundary
        headers: {
          'Content-Type': undefined
        },
        // The catch below shows the one error toast for a failed upload; the
        // global interceptor would otherwise stack a duplicate on top of it.
        skipGlobalToast: true
      }
    )

     const uploadResponse = response.data
     setImportId(uploadResponse.import_id)
    const columnsResponse = await getImportColumns(
    uploadResponse.import_id
    
  )


  if (!columnsResponse) {
    return null
  }

  
 
   const mappedColumnsCount = columnsResponse?.mapped_columns?.length

      if (mappedColumnsCount > MAX_IMPORT_COLUMNS) {
        showError(`File should not contain more than ${MAX_IMPORT_COLUMNS} columns`)

        return 'Too many columns'
      }

      setRawFileHeadersData(response?.data?.data?.Data)
       setImportColumns(columnsResponse)
  setMappedColumns(columnsResponse?.mapped_columns)

      setPiiColumns(updatedPiiColumns(columnsResponse?.system_columns))
      setSystemColumns(columnsResponse?.system_columns)
      showSuccess('File Uploaded successfully!')

    

    return response?.data
   
  } catch (error) {
    // A 401 is still toasted by the interceptor (it always is — the session is
    // ending), so don't add a second toast for it here.
    if (error?.response?.status !== 401) {
      showError(
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        'Failed to upload file'
      )
    }

    return null
  }
}
const getImportColumns = async (importId) => {
  if (!importId) {
    console.error('Import ID is required')
    return null
  }

  try {
    const response = await api.get(
      `${audienceImportUrl}/${importId}/columns`
    )

    return response?.data || null
  } catch (error) {
    console.error('Failed to fetch import columns:', error)

    showError(
      error?.response?.data?.detail ||
      'Failed to fetch import columns'
    )

    return null
  }
}

  

  const startImport = async import_id => {
  try {
    const response = await api.post(
      `${audienceImportUrl}/${import_id}/start`, {
        create_segment: createSegment
      }
    )

    if (createSegment) {
      const fileName = files?.[0]?.name
        ? files[0].name.replace(/\.[^/.]+$/, '')
        : 'imported_segment'

      showSuccess(
        `The audience import has been successfully started. A segment '${fileName}' will also be created from the import tags.`
      )
    } else {
      showSuccess(
        'Import started successfully! Please wait a few seconds...'
      )
    }
 pollImportStatus(import_id)
    if (response?.status === 202) {
      setTimeout(() => {
        router.push('/customer-data-platform/all-audience')
      }, 3000)
    }
     refreshAudienceList()
  } catch (error) {
    showError(
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      'Failed to start import. Please try again.'
    )
  }
}
const getImportStatus = async import_id => {
  try {
    const response = await api.get(
      `${audienceImportUrl}/${import_id}`
    )

    return response?.data || null
  } catch (error) {
    console.error('Failed to get import status:', error)

    return null
  }
}
const pollImportStatus = async import_id => {
  const maxAttempts = 30
  let attempts = 0

  const checkStatus = async () => {
    attempts += 1

    const importData = await getImportStatus(import_id)


    if (!importData) {
      return
    }

    if (importData.import_status === 'completed') {
      showSuccess('Audience import completed successfully!')

      router.push('/customer-data-platform/all-audience')
      refreshAudienceList()
      return
    }

    if (importData.import_status === 'failed') {
      showError('Audience import failed.')
      return
    }

    if (attempts >= maxAttempts) {
      showError('Import is taking longer than expected.')
      return
    }

    // Check again after 2 seconds
    setTimeout(checkStatus, 2000)
  }

  checkStatus()
}


const updateImportData = async (data) => {
  try {

    const mappings = (data?.mapped_columns || [])
      .filter(
        column =>
          column?.header_name &&
          column?.matched_system_column
      )
      .map(column => ({
        source: column.header_name,
        destination: column.matched_system_column
      }))

    const payload = {
      user_verified_mapping: {
        mappings
      },
      tags: Array.isArray(data?.tags) ? data.tags : []
    }

  

    const response = await api.put(
      `${audienceImportUrl}/${importId}/mapping`,
      payload
    )


    setPreviewData(response?.data)

    showSuccess('Audience columns mapped successfully!')

    return response?.data
  } catch (error) {
    console.error('Mapping API failed:', error)

    console.error(
      'Backend error:',
      error?.response?.data
    )

    showError(
      error?.response?.data?.detail ||
      'Failed to save audience column mapping'
    )

    return null
  }
}



  const getIdFromLocation = response => {
    const location = response?.headers?.location || response?.headers?.Location
    if (!location) return null
    const segments = location.split('/').filter(Boolean)
    return segments[segments.length - 1] || null
  }

  const getImports = async () => {
    try {
      const response = await api.get(audienceImportUrl)
      return response?.data ?? []
    } catch (error) {
      showError(error?.response?.data?.detail || 'Failed to load imports')
      return []
    }
  }

 

  const setImportMapping = async (importId, mappings, customFieldsColumn) => {
    try {
      const response = await api.put(`${audienceImportUrl}/${importId}/mapping`, {
        user_verified_mapping: {
          mappings,
          ...(customFieldsColumn ? { custom_fields_column: customFieldsColumn } : {})
        }
      })
      return response?.data ?? null
    } catch (error) {
      showError(error?.response?.data?.detail || 'Failed to save column mapping')
      return null
    }
  }

  // 400 if no uploaded file or no mapping confirmed yet. 409 if already started - that's the
  // double-ingest guard, don't retry through it.
  const startImportIngestion = async importId => {
    try {
      const response = await api.post(`${audienceImportUrl}/${importId}/start`)
      showSuccess('Import started successfully!...Please wait a few seconds!...')
      return response?.data ?? null // { import_id, sync_id, filename, import_status }
    } catch (error) {
      showError(error?.response?.data?.detail || 'Failed to start import')
      return null
    }
  }

  const getImport = async importId => {
    try {
      const response = await api.get(`${audienceImportUrl}/${importId}`)
      return response?.data ?? null
    } catch (error) {
      showError(error?.response?.data?.detail || 'Failed to load import')
      return null
    }
  }

  const getImportStats = async importId => {
    try {
      // Tenant-wide aggregate, not scoped per import - unconfirmed whether import_id is honored
      // here. Not part of the confirmed 5-step flow above; left as-is, uncontradicted by it.
      const response = await api.get(`${audienceImportUrl}/stats`, { params: { import_id: importId } })
      return response?.data
    } catch {
      return null
    }
  }


  const fetchAudienceEvents = async (universal_profile_id, cursor = null) => {
    try {
      const response = await api.get(`${audiencesUrl}/${universal_profile_id}/events`, {
        params: { cursor, limit: rowsPerPage }
      })
      
      setSelectedAudienceEvents(response?.data?.events ?? [])
      setEventCursor(response?.data?.next_cursor ?? null)
      setHasMoreEvents(!!response?.data?.next_cursor)
    } catch {}
  }

  const highlightText = (text, highlightSearchTerm) => {
    if (!highlightSearchTerm.trim()) {
      return text
    }
    const parts = text?.split(new RegExp(`(${highlightSearchTerm})`, 'gi'))

    return parts.map((part, index) =>
      part.toLowerCase() === highlightSearchTerm.toLowerCase() ? (
        <Box component='span' key={`${part}-${index}`} sx={{ backgroundColor: 'yellow' }}>
          {part}
        </Box>
      ) : (
        part
      )
    )
  }

  const filterDataTypeIcon = data_type => {
    if (data_type === 'string') {
      return <img src={`/images/text.png`} alt='text' width={40} height={30} loading='lazy' />
    } else if (data_type === 'int' || data_type === 'float') {
      return <img src={`/images/number.png`} alt='number' width={35} height={25} loading='lazy' />
    } else if (data_type === 'null') {
      return null
    } else if (data_type === 'date') {
      return <img src={`/images/date.png`} alt='date' width={40} height={30} loading='lazy' />
    } else if (data_type === 'jsonb') {
      return <img src={`/images/json.png`} alt='jsonb' width={40} height={30} loading='lazy' />
    } else {
      return null
    }
  }

  const formatToDateString = dateString => {
    if (!dateString || dateString === '""') {
      return ''
    } else {
      const cleanedDateString = dateString.replace(/(^["'])|(["']$)/g, '')
      const date = new Date(cleanedDateString)

      if (isNaN(date.getTime())) {
        return ''
      }

      const year = date.getFullYear()

      const month = String(date.getMonth() + 1).padStart(2, '0')

      const day = String(date.getDate()).padStart(2, '0')

      return `${month}/${day}/${year}`
    }
  }

  const normalizeField = field => {
    if (field === `""` || field === '' || field === '****') {
      return ''
    } else {
      return field
    }
  }

  const updatedPiiColumns = columnsInput => {
    const filteredPiiColumns = columnsInput?.filter(item => item.is_pii === true).map(item => item.system_column)

    return filteredPiiColumns
  }

  function formatDateTime(isoString) {
    const date = new Date(isoString)

    // Intl.DateTimeFormat throws on an invalid date; render blanks instead.
    if (!isoString || Number.isNaN(date.getTime())) {
      return { time: '', fullDate: '' }
    }

    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true }
    const time = new Intl.DateTimeFormat('en-US', timeOptions).format(date)

    const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' }
    const fullDate = new Intl.DateTimeFormat('en-US', dateOptions).format(date)

    return { time, fullDate }
  }

  // The audience filter bar has only a tags filter, sourced from CDP. Returns
  // the initial selected-filters map (no other filters) for the page.
  const getAudienceFilterList = async () => {
    setAudienceOtherFilterList([])
    await getAudienceTagOptions()
    return {}
  }

  const getAudienceTagOptions = async (search = '') => {
    try {
      const tags = await getAudienceTags(search)
      setAudienceFilterList(tags)
      setHasTagFilter(tags.length > 0)
    } catch {
      setAudienceFilterList([])
      setHasTagFilter(false)
    }
  }

  const statusIcon = iconName => {
    if (iconName === 'sms') {
      return <Icon color='#1c9ae9' icon='mdi:message-text-outline' fontSize={20} />
    }
    if (iconName === 'whatsapp') {
      return <Icon color='#228B22' icon='mdi:whatsapp' fontSize={20} />
    }
    if (iconName === 'email') {
      return <Icon color='#B87333' icon='mdi:email-outline' fontSize={20} />
    }
    if (iconName === 'rcs') {
      return <Icon color='#0066CC' icon='mdi:message-badge-outline' fontSize={20} />
    }

    return null
  }

  const values = useMemo(() => ({
    eventCursor,
    setEventCursor,
    hasMoreEvents,
    setHasMoreEvents,
    eventSearchTerm,
    setEventSearchTerm,
    selectedAudienceEvents,
    setSelectedAudienceEvents,
    csvData,
    setCsvData,
    importMethod,
    setImportMethod,
    isIdentifierColumn,
    isPiiChanged,
    setIsPiiChanged,
    openConfirmPii,
    setOpenConfirmPii,
    piiColumns,
    setPiiColumns,
    deleteId,
    setDeleteId,
    deleteAudienceName,
    setDeleteAudienceName,
    openDelete,
    setOpenDelete,
    selectedColumns,
    setSelectedColumns,
    files,
    setFiles,
    mappedColumns,
    setMappedColumns,
    importColumns,
    setImportColumns,
    systemColumns,
    setSystemColumns,
    selectedTags,
    setSelectedTags,
    autoFileTags,
    setAutoFileTags,
    tagsData,
    setTagsData,
    getAllTags,
    rawFileHeadersData,
    setRawFileHeadersData,
    updatedRawFileHeadersData,
    setUpdatedRawFileHeadersData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchTerm,
    setSearchTerm,
    rows,
    setRows,
    selectedRows,
    setSelectedRows,
    rowCount,
    setRowCount,
    sortOrder,
    setSortOrder,
    sortOrderBy,
    setSortOrderBy,
    previewData,
    setPreviewData,
    audiencesListRow,
    setAudiencesListRow,
    selectedAudience,
    filterDataTypeIcon,
    setSelectedAudience,
    selectedAudienceID,
    setSelectedAudienceID,
    systemColumnList,
    setSystemColumnList,
    columnStatusList,
    setColumnStatusList,
    dataStatus,
    setDataStatus,
    maskField,
    setMaskField,
    isUniqueColumnExists,
    setIsUniqueColumnExists,
    schema,
    defaultValues,
    normalizeField,
    formatToDateString: formatToDateString,
    highlightText: highlightText,
    formatDateTime: formatDateTime,
    statusIcon: statusIcon,
    getAudienceList: getAudienceList,
    getAudience: getAudience,
    saveAudienceData: saveAudienceData,
    deleteAudience: deleteAudience,
    audienceBulkAction: audienceBulkAction,
    nextCursor,
    listVersion,
    refreshAudienceList,
    uploadFilesData: uploadFilesData,
    updateImportData: updateImportData,
    startImport: startImport,
    getImports: getImports,
    //uploadImportFile: uploadImportFile,
    getImportColumns: getImportColumns,
    setImportMapping: setImportMapping,
    startImportIngestion: startImportIngestion,
    getImportStats: getImportStats,
    getImport: getImport,
    fetchAudienceEvents: fetchAudienceEvents,
    getAudienceFilterList: getAudienceFilterList,
    getAudienceTagOptions:getAudienceTagOptions,
    getAudienceTags,
    audienceFilterList,
    setAudienceFilterList,
    audienceOtherFilterList,
    setAudienceOtherFilterList,
    hasTagFilter,
    setHasTagFilter,
    isNewFileUploaded,
    setIsNewFileUploaded,
    createSegment,
    setCreateSegment
  }))

  return <AudienceContext.Provider value={values}>{children}</AudienceContext.Provider>
}
AudienceProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export { AudienceContext, AudienceProvider, defaultProvider }
